import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useKothDetails } from "@/lib/hooks/useApi";
import AnimatedLoading from "@/components/AnimatedLoading";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { cn, generateTextShadow, REWARD_ICONS } from "@/lib/utils";
import { UserRankList } from "@/components/UserRankList";
import type { KothCurrentResponse, Player } from "@/lib/types/api";
import { NavigationKOTHDetails } from "./-components/NavigationKOTHDetails";
import { t } from "@/lib/i18n";

import { ReportPlayerDialog } from "@/components/ReportPlayerDialog";
import { ReportModeToggle } from "@/components/ReportModeToggle";
import { usePrivy } from "@privy-io/react-auth";
import { useStats, useCanReportBatch } from "@/lib/hooks/useApi";

export const Route = createFileRoute("/(game)/koth/$kothId")({
  component: DinnerComponent,
});

function DinnerComponent() {
  const { kothId } = Route.useParams();
  const { data: kothDetails, isLoading } = useKothDetails(kothId);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [reportTarget, setReportTarget] = useState<Player | null>(null);
  const [isReportMode, setIsReportMode] = useState(false);
  const { user } = usePrivy();
  const { data: currentUserStats } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = currentUserStats?.embeddedWallet;

  const players = (kothDetails as KothCurrentResponse)?.players || [];
  const reportedIds = players
    .map((p) => p.ownerProfile.userId)
    .filter((id): id is number => id != null);
  const { data: canReportStatuses } = useCanReportBatch(
    reportedIds,
    currentUserStats?.userId,
  );

  if (isLoading) {
    return <AnimatedLoading />;
  }

  if (!kothDetails) {
    return <AnimatedLoading />;
  }

  // Inline the ended UI (mimic of KOTHDetails) without importing it
  const kd = kothDetails as KothCurrentResponse;

  const sortedPlayers = [...players]
    .sort((a, b) => {
      const aValue = a.squadDetails?.squadValue || 0;
      const bValue = b.squadDetails?.squadValue || 0;
      return bValue - aValue;
    })
    .map((p) => ({
      ...p,
      ownerProfile: {
        ...p.ownerProfile,
        userPubkey:
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (p.ownerProfile as any).userPubkey ??
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (p.ownerProfile as any).userPubKey ??
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (p.squadDetails as any)?.owner ??
          "",
      },
    }));
  const canReportMap = new Map(
    canReportStatuses?.map((s) => [s.reportedId, s.canReport]) ?? [],
  );

  // Determine match outcome
  // Players array always has exactly 2 players:
  // - players[0] = host/currentKing (if currentKing exists)
  // - players[1] = challenger
  const currentKing = kd?.currentKing;
  const winnerSquadId = kd?.winnerSquadId;
  const hostPlayer = players[0];
  const challengerPlayer = players[1];

  // King defended: currentKing exists AND winner is the host/currentKing
  const kingDefended =
    currentKing !== null &&
    winnerSquadId !== null &&
    winnerSquadId === hostPlayer?.squadAddress;

  // Challenger won: currentKing exists AND winner is the challenger
  const challengerWon =
    currentKing !== null &&
    winnerSquadId !== null &&
    winnerSquadId === challengerPlayer?.squadAddress;

  const newKingCrowned = currentKing !== null && currentKing.streakCount === 0;

  const baseReward = kd?.baseReward ?? 0;
  const betAmount = kd?.betAmount ?? 0;
  const kingTrophies = currentKing?.kingTrophies ?? 0;
  const challengerTrophies = currentKing?.challengerTrophies ?? 0;
  const dethronedKingPayout = currentKing?.dethronedKingPayout ?? 0;
  const nextStreakBonus = currentKing?.nextStreakBonus ?? 0;
  const dethronedBonus = currentKing?.dethronedChallengerBonus ?? 0;
  const defaultTrophies = kd?.trophies ?? 0;
  const loserTrophiesRaw =
    kd?.loserTrophies ??
    (kd as { loserThropies?: number } | null)?.loserThropies ??
    0;
  const loserTrophies = Math.abs(loserTrophiesRaw);

  type Outcome = "kingDefended" | "challengerWon" | "newKingCrowned" | "none";

  const outcome: Outcome = kingDefended
    ? "kingDefended"
    : challengerWon
      ? "challengerWon"
      : newKingCrowned
        ? "newKingCrowned"
        : "none";

  const winnerRewards = (() => {
    switch (outcome) {
      case "kingDefended":
        return {
          sol: baseReward + nextStreakBonus,
          trophies: kingTrophies || defaultTrophies,
        };
      case "challengerWon":
        return {
          sol: baseReward + dethronedBonus,
          trophies: challengerTrophies || defaultTrophies,
        };
      case "newKingCrowned":
        return {
          sol: baseReward,
          trophies: defaultTrophies,
        };
      default:
        return {
          sol: baseReward,
          trophies: defaultTrophies,
        };
    }
  })();

  return (
    <div className="relative flex-1 flex flex-col h-full">
      <FloatingLeftButton
        onClick={() => window.history.back()}
        className="absolute flex items-center gap-4 top-4 left-0 z-40"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      {/* Inline ended screen */}
      <div
        className={cn(
          "w-full flex-1 min-h-0",
          "flex flex-col items-stretch overflow-hidden",
          "relative before:absolute before:inset-0 before:bg-black/15 before:z-10"
        )}
        style={{
          imageRendering: "pixelated",
          background: "url(https://cdn.navalabs.io/assets/bg-full_cJmDRn.png)",
          backgroundSize: "100% 100%",
          backgroundPosition: "top",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className={cn(
            "w-full h-full min-h-0 flex-1",
            "flex flex-col",
            "overflow-hidden relative z-20"
          )}
        >
          <ReportModeToggle
            isActive={isReportMode}
            onToggle={() => setIsReportMode((prev) => !prev)}
          />
          {/* Tournament Info */}
          <div className="pt-4 pb-2 shrink-0 text-center">
            <h2 className="text-white font-bold text-3xl uppercase">
              {t("koth.kingOfTheHill")}
            </h2>
            <p className="text-white/90 text-sm mt-2">{t("koth.matchCompleted")}</p>
            {isReportMode && (
              <p
                className="text-white text-sm font-bold mt-1 uppercase animate-pulse"
                style={{
                  textShadow: generateTextShadow(1),
                }}
              >
                {t("tournamentEnded.selectPlayerToReport")}
              </p>
            )}
          </div>

          {/* Results Section */}
          {sortedPlayers.length > 0 ? (
            <div className="px-2 flex-1 flex flex-col gap-1 overflow-y-auto pb-20">
              {sortedPlayers.map((player, playerIndex) => {
                const rank = playerIndex + 1;
                const isWinner = rank === 1;
                const isHost =
                  player.squadAddress === hostPlayer?.squadAddress;
                const isChallenger =
                  player.squadAddress === challengerPlayer?.squadAddress;

                let solValue = isWinner ? winnerRewards.sol : betAmount;
                let solDirection: boolean | undefined = isWinner
                  ? solValue > 0
                  : solValue === 0
                    ? undefined
                    : false;

                let trophiesValue = isWinner
                  ? winnerRewards.trophies
                  : loserTrophies;
                let trophyDirection: boolean | undefined =
                  trophiesValue === 0 ? undefined : true;

                if (!isWinner && isHost) {
                  // Host lost = dethroned king. Use dethronedKingPayout
                  // instead of -betAmount, even when challengerWon can't be
                  // confirmed via currentKing/winnerSquadId.
                  solValue = newKingCrowned ? -betAmount : dethronedKingPayout;
                  solDirection = solValue === 0 ? undefined : solValue > 0;
                  trophiesValue = loserTrophies;
                  trophyDirection = trophiesValue === 0 ? undefined : true;
                }

                if (!isWinner && isChallenger) {
                  trophiesValue = loserTrophies;
                  trophyDirection = trophiesValue === 0 ? undefined : true;
                }

                const solDisplayValue = Math.abs(solValue);
                const trophiesDisplayValue = Math.abs(trophiesValue);

                const isSelf =
                  gameWalletAddress === player.ownerProfile.userPubkey;

                return (
                  <div
                    key={playerIndex}
                    className={cn(
                      "relative transition-opacity duration-200",
                      isReportMode &&
                        isSelf &&
                        "opacity-40 pointer-events-none",
                    )}
                  >
                    <UserRankList
                      player={player}
                      rank={rank}
                      isWinner={isWinner}
                      isSelf={isSelf}
                      canReport={canReportMap.get(player.ownerProfile.userId ?? -1) ?? false}
                      isReportMode={isReportMode && !isSelf}
                      prizes={[
                        {
                          icon: REWARD_ICONS[0],
                          value: solDisplayValue,
                          alt: t("koth.altTexts.sol"),
                          isPositive: solDirection,
                        },
                        {
                          icon: REWARD_ICONS[1],
                          value: trophiesDisplayValue,
                          alt: t("koth.altTexts.trophy"),
                          isPositive: trophyDirection,
                        },
                      ]}
                      onClick={() => {
                        if (isReportMode) {
                          if (!isSelf) {
                            setReportTarget(player);
                          }
                          return;
                        }
                        setSelectedPlayer(player);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-white text-lg font-bold mb-2 animated">
                {t("koth.matchCompleted")}
              </h3>
              <p className="text-gray-400 text-sm">
                {t("koth.finalScoresAvailableSoon")}
              </p>
            </div>
          )}

          {/* Navigation Section */}
          <NavigationKOTHDetails
            kothDetails={kd}
            sortedPlayers={sortedPlayers}
            selectedPlayer={selectedPlayer}
            onBack={() => setSelectedPlayer(null)}
            className="h-full max-h-[40svh] w-full"
          />
        </div>
      </div>
      <ReportPlayerDialog
        open={reportTarget !== null}
        onOpenChange={(open) => { if (!open) { setReportTarget(null); setIsReportMode(false); } }}
        reportedId={reportTarget?.ownerProfile.userId}
        reportedUsername={reportTarget?.ownerProfile.username}
        reportedProfilePicture={reportTarget?.ownerProfile.profilePicture}
        reportedFrameUrl={reportTarget?.ownerProfile.activeFrameUrl}
        reportedIsVerified={reportTarget?.ownerProfile.isVerified}
        canReportPlayer={canReportMap.get(reportTarget?.ownerProfile.userId ?? -1) ?? false}
        proofId={kothId}
        playerDetails={reportTarget?.squadDetails.playerDetails}
      />
    </div>
  );
}
